import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Row, Col,
    Input, InputGroup, Table
} from "reactstrap";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import DataTable from "react-data-table-component";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { H3, P } from "../../AbstractElements";
import { Search, RotateCcw, FileText, Download } from 'react-feather';
import DatePicker from "react-datepicker";
import { useConfig } from "../../Context/ConfigContext";
import { useAuth } from "../../Context/AuthContext";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

interface ProfitabilityData {
    date: string;
    total_revenue: number;
    total_bystander_cost: number;
    total_expense: number;
    net_profit: number;
}

interface ProfitabilityDataWithFooter extends Partial<ProfitabilityData> {
    isFooter?: boolean;
}

export default function ProfitabilityReport() {
    const [data, setData] = useState<ProfitabilityData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("date");
    const [sortDir, setSortDir] = useState("desc");
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [summary, setSummary] = useState({ total_revenue: 0, total_bystander_cost: 0, total_expense: 0, net_profit: 0 });
    const [pdfLoading, setPdfLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const { config } = useConfig();
    const { hasPermission } = useAuth();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const response: any = await apiPost(`report/profitability-report?page=${currentPage}`, {
                per_page: perPage,
                search: debouncedSearch,
                start_date: startDate,
                end_date: endDate,
                sort_by: sortBy,
                sort_direction: sortDir
            });
            setData(response.data.data || []);
            setTotalRows(response.data.total || 0);
            setSummary(response.summary || { total_revenue: 0, total_bystander_cost: 0, total_expense: 0, net_profit: 0 });
        } catch (error) {
            toast.error("Failed to load profitability report");
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, debouncedSearch, startDate, endDate, sortBy, sortDir]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, startDate, endDate]);

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || 'date');
        setSortDir(sortDirection);
    };

    const fetchAllData = async () => {
        try {
            const response: any = await apiPost(`report/profitability-report?page=1`, {
                per_page: -1,
                search: debouncedSearch,
                start_date: startDate,
                end_date: endDate,
                sort_by: sortBy,
                sort_direction: sortDir
            });
            return response.data.data || [];
        } catch (error) {
            console.error("Error fetching all data:", error);
            return [];
        }
    };

    const handleReset = () => {
        setSearchTerm("");
        setStartDate("");
        setEndDate("");
        setCurrentPage(1);
    };

    const formatDateForDisplay = (dateString: string) => {
        if (!dateString) return "N/A";
        const dateObj = new Date(dateString);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = String(dateObj.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    };

    const exportToExcel = async (exportDataItems: any[]) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Profitability Report');

        const headerStyle: Partial<ExcelJS.Style> = {
            font: { bold: true, color: { argb: 'FFFFFFFF' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00695C' } },
            alignment: { vertical: 'middle', horizontal: 'center' }
        };

        worksheet.columns = [
            { header: 'Sl.No', key: 'sl', width: 8 },
            { header: 'Date', key: 'date', width: 20 },
            { header: `Total Revenue (${config.currency})`, key: 'revenue', width: 20 },
            { header: `Bystander Cost (${config.currency})`, key: 'b_cost', width: 20 },
            { header: `Operational Expense (${config.currency})`, key: 'op_exp', width: 25 },
            { header: `Net Profit (${config.currency})`, key: 'net_profit', width: 20 },
        ];

        worksheet.getRow(1).eachCell((cell) => {
            cell.style = headerStyle;
        });

        exportDataItems.forEach((item, index) => {
            const row = worksheet.addRow({
                sl: index + 1,
                date: formatDateForDisplay(item.date),
                revenue: Number(item.total_revenue || 0),
                b_cost: Number(item.total_bystander_cost || 0),
                op_exp: Number(item.total_expense || 0),
                net_profit: Number(item.net_profit || 0)
            });

            row.eachCell((cell) => {
                cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            });
        });

        const summaryRow = worksheet.addRow({
            sl: '',
            date: 'TOTAL',
            revenue: summary.total_revenue,
            b_cost: summary.total_bystander_cost,
            op_exp: summary.total_expense,
            net_profit: summary.net_profit
        });

        summaryRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Profitability_Report_${formatDate(new Date())}.xlsx`);
    };

    const exportToPDF = (exportDataItems: any[]) => {
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.text("Profitability Report", 14, 15);

        const currencyMap: Record<string, string> = { 
            '₹': 'Rs.', '€': 'EUR', '£': 'GBP', '¥': 'YEN',
            '₽': 'RUB', '₩': 'KRW', '฿': 'THB', '₪': 'ILS', '₫': 'VND', '₱': 'PHP' 
        };
        const pdfCurrency = currencyMap[config.currency] || config.currency;
        const tableColumn = ["Sl.No", "Date", `Total Revenue (${pdfCurrency})`, `Bystander Cost (${pdfCurrency})`, `Expense (${pdfCurrency})`, `Net Profit (${pdfCurrency})`];
        const tableRows = exportDataItems.map((item, index) => {
            return [
                index + 1,
                formatDateForDisplay(item.date),
                Number(item.total_revenue || 0).toFixed(2),
                Number(item.total_bystander_cost || 0).toFixed(2),
                Number(item.total_expense || 0).toFixed(2),
                Number(item.net_profit || 0).toFixed(2)
            ];
        });

        tableRows.push([
            "",
            "TOTAL",
            Number(summary.total_revenue || 0).toFixed(2),
            Number(summary.total_bystander_cost || 0).toFixed(2),
            Number(summary.total_expense || 0).toFixed(2),
            Number(summary.net_profit || 0).toFixed(2)
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            theme: 'grid',
            headStyles: { fillColor: [0, 105, 92] },
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 15 },
                2: { halign: 'right' },
                3: { halign: 'right', textColor: [211, 47, 47] },
                4: { halign: 'right', textColor: [211, 47, 47] },
                5: { fontStyle: 'bold', halign: 'right', textColor: [0, 105, 92] }
            },
            didParseCell: (cellData: any) => {
                if (cellData.row.index === tableRows.length - 1) {
                    cellData.cell.styles.fontStyle = 'bold';
                    cellData.cell.styles.fillColor = [245, 245, 245];
                }
            }
        });
        doc.save(`Profitability_Report_${formatDate(new Date())}.pdf`);
    };

    const columns: any[] = [
        {
            name: <div><span className="fw-bold">Sl. No.</span></div>,
            selector: (row: ProfitabilityDataWithFooter, index: number) =>
                row.isFooter ? "" : (currentPage - 1) * perPage + (index ?? 0) + 1,
            width: "100px",
        },
        {
            name: <div><span className="fw-bold">Date</span></div>,
            cell: (row: ProfitabilityDataWithFooter) => {
                if (row.isFooter) return <span className="fw-bold" style={{ fontSize: '14px', color: '#00695c' }}>TOTAL:</span>;
                return (
                    <div className="fw-bold text-dark py-2">
                        {formatDateForDisplay(row.date || "")}
                    </div>
                );
            },
            sortable: (row: ProfitabilityDataWithFooter) => !row.isFooter,
            sortField: "date",
            width: "150px",
        },
        {
            name: <div><span className="fw-bold">Total Revenue ({config.currency})</span></div>,
            sortable: (row: ProfitabilityDataWithFooter) => !row.isFooter,
            sortField: "total_revenue",
            cell: (row: ProfitabilityDataWithFooter) => (
                <div className="w-100 fw-bold" style={{ fontSize: row.isFooter ? '14px' : 'inherit', color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {Number(row.total_revenue || 0).toLocaleString()}
                </div>
            )
        },
        {
            name: <div><span className="fw-bold">Bystander Cost ({config.currency})</span></div>,
            sortable: (row: ProfitabilityDataWithFooter) => !row.isFooter,
            sortField: "total_bystander_cost",
            cell: (row: ProfitabilityDataWithFooter) => (
                <div className="w-100 fw-bold text-danger px-3" style={{ fontSize: row.isFooter ? '14px' : 'inherit', color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {Number(row.total_bystander_cost || 0).toLocaleString()}
                </div>
            )
        },
        {
            name: <div><span className="fw-bold">Operational Expense ({config.currency})</span></div>,
            sortable: (row: ProfitabilityDataWithFooter) => !row.isFooter,
            sortField: "total_expense",
            cell: (row: ProfitabilityDataWithFooter) => (
                <div className="w-100 fw-bold text-warning px-3" style={{ fontSize: row.isFooter ? '14px' : 'inherit', color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {Number(row.total_expense || 0).toLocaleString()}
                </div>
            )
        },
        {
            name: <div><span className="fw-bold">Net Profit ({config.currency})</span></div>,
            sortable: (row: ProfitabilityDataWithFooter) => !row.isFooter,
            sortField: "net_profit",
            cell: (row: ProfitabilityDataWithFooter) => (
                <div className="w-100 fw-bold text-success px-3" style={{ fontSize: row.isFooter ? '14px' : 'inherit', color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {Number(row.net_profit || 0).toLocaleString()}
                </div>
            )
        },
    ];

    const columnsData: ProfitabilityDataWithFooter[] = data.length > 0 ? [
        ...data,
        {
            isFooter: true,
            total_revenue: summary.total_revenue,
            total_bystander_cost: summary.total_bystander_cost,
            total_expense: summary.total_expense,
            net_profit: summary.net_profit
        }
    ] : [];

    const conditionalRowStyles = [
        {
            when: (row: ProfitabilityDataWithFooter) => !!row.isFooter,
            style: {
                fontWeight: "800",
                backgroundColor: "#f8f9fa",
                color: "#1e3a8a",
                borderTop: "2px solid #dee2e6",
                fontSize: "16px",
            },
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                color: '#308e87',
                minHeight: '50px',
                borderBottom: '2px solid #308e87',
            },
        },
        headCells: {
            style: { fontSize: '16px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px', borderBottom: "1px solid #dee2e6" },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Profitability Report" parent="Reports" title="Audit Stream" />
            <Container fluid>
                <Card className="border-0 shadow-sm">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <H3 className="text-primary mb-0">Profitability Report</H3>
                            <div className="d-flex gap-2">
                                {hasPermission('profitability-report.download_pdf') && (
                                    <Button
                                        color="success"
                                        onClick={async () => {
                                            setPdfLoading(true);
                                            const allData = await fetchAllData();
                                            exportToPDF(allData);
                                            setPdfLoading(false);
                                        }}
                                        disabled={pdfLoading}
                                        className="d-flex align-items-center gap-2 px-3 shadow-sm border-0"
                                        style={{ borderRadius: '6px' }}
                                        title="Export PDF"
                                    >
                                        {pdfLoading ? <Spinner size="sm" /> : <FileText size={16} />}
                                        <span className="fw-bold small">{pdfLoading ? 'PREPARING...' : 'PDF'}</span>
                                    </Button>
                                )}
                                {hasPermission('profitability-report.download_excel') && (
                                    <Button
                                        color="warning"
                                        onClick={async () => {
                                            setExcelLoading(true);
                                            const allData = await fetchAllData();
                                            await exportToExcel(allData);
                                            setExcelLoading(false);
                                        }}
                                        disabled={excelLoading}
                                        className="d-flex align-items-center gap-2 px-3 shadow-sm border-0 text-white"
                                        style={{ backgroundColor: '#ffaf00', borderRadius: '6px' }}
                                        title="Export Excel"
                                    >
                                        {excelLoading ? <Spinner size="sm" /> : <Download size={16} />}
                                        <span className="fw-bold small">{excelLoading ? 'PREPARING...' : 'EXCEL'}</span>
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className=" mb-4">
                            <Row className="align-items-center justify-content-end g-3">
                                <Col xl={5} lg={7} md={12} className="d-flex align-items-center gap-2">
                                    <span className="small text-muted fw-bold text-uppercase ms-2" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>FROM:</span>
                                    <div style={{ flex: 1 }}>
                                        <DatePicker
                                            selected={startDate ? new Date(startDate) : null}
                                            onChange={(date: Date) => setStartDate(date ? formatDate(date) : "")}
                                            placeholderText="Start date"
                                            className="form-control bg-white"
                                            dateFormat="yyyy-MM-dd"
                                            isClearable
                                        />
                                    </div>
                                    <span className="small text-muted fw-bold text-uppercase" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>TO:</span>
                                    <div style={{ flex: 1 }}>
                                        <DatePicker
                                            selected={endDate ? new Date(endDate) : null}
                                            onChange={(date: Date) => setEndDate(date ? formatDate(date) : "")}
                                            placeholderText="End date"
                                            className="form-control bg-white"
                                            dateFormat="yyyy-MM-dd"
                                            isClearable
                                        />
                                    </div>
                                </Col>

                                <Col xl={3} lg={5} md={12}>
                                    <InputGroup className="shadow-sm">
                                        <Input
                                            placeholder="Search by date..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="bg-white"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>
                        </div>

                        <div className="border rounded">
                            <DataTable
                                columns={columns}
                                data={columnsData}
                                progressPending={loading}
                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                onChangeRowsPerPage={(newSize: number, page: number) => setPerPage(newSize)}
                                onChangePage={(page: number) => setCurrentPage(page)}
                                sortServer
                                onSort={handleSort}
                                highlightOnHover
                                customStyles={customStyles}
                                conditionalRowStyles={conditionalRowStyles}
                                noDataComponent={
                                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                        <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                            <CardBody>
                                                <h6 className="fw-semibold mb-1">No Records Found</h6>
                                                <small className="text-muted">No financial data matching your criteria.</small>
                                            </CardBody>
                                        </Card>
                                    </div>
                                }
                            />
                        </div>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
